import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Truck, ShieldCheck, Headphones } from "lucide-react";
import { Hero } from "@/components/Hero";
import { ProductCard } from "@/components/ProductCard";
import { Reveal } from "@/components/Reveal";
import { api } from "@/lib/api";
import { useModals } from "@/context/ModalContext";

const CATEGORIES = [
  { key: "sauna", label: "Saunas", img: "https://images.unsplash.com/photo-1605614307370-f7a1e58ae751?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", copy: "Infrared, traditional, barrel & cabin" },
  { key: "cold-plunge", label: "Cold Plunges", img: "https://images.unsplash.com/photo-1701895853566-bd08afa0c422?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", copy: "Chiller tubs & cedar plunges" },
  { key: "hot-tub", label: "Hot Tubs", img: "https://images.unsplash.com/photo-1634253539564-8887939e239f?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", copy: "Luxury spas & swim spas" },
  { key: "accessory", label: "Accessories", img: "https://images.unsplash.com/photo-1614759258004-39da973d3268?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", copy: "Heaters, salts, oils & more" },
];

const TRUST = [
  { icon: Truck, t: "White Glove Delivery", d: "Premium delivery & installation." },
  { icon: ShieldCheck, t: "Authorized Dealer", d: "Harvia, Kohler, Dundalk & more." },
  { icon: Headphones, t: "Expert Guidance", d: "Free 1-on-1 recovery consultations." },
  { icon: Sparkles, t: "AI Matchmaking", d: "Find your perfect sanctuary in minutes." },
];

export default function Home() {
  const [heroImage, setHeroImage] = useState("https://images.unsplash.com/photo-1713270176378-45fbf4a27099?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600");
  const [bestSellers, setBestSellers] = useState([]);
  const { openConsultation } = useModals();

  useEffect(() => {
    api.getConfig().then((c) => c.heroImage && setHeroImage(c.heroImage)).catch(() => {});
    api.getProducts({ sort: "rating" }).then((d) => {
      setBestSellers((d.products || []).filter((p) => p.bestSeller).slice(0, 4));
    }).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Hero heroImage={heroImage} />

      {/* Trust bar */}
      <section className="bg-charcoal border-y border-white/10">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {TRUST.map((t) => (
            <div key={t.t} className="flex items-start gap-3">
              <t.icon className="text-gold shrink-0 mt-0.5" size={22} />
              <div>
                <p className="text-cream text-sm font-medium">{t.t}</p>
                <p className="text-cream/55 text-xs">{t.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20">
        <Reveal>
          <p className="overline text-center">Shop By Category</p>
          <h2 className="font-serif text-3xl sm:text-4xl text-cream text-center mt-3">Build Your Sanctuary</h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {CATEGORIES.map((c, i) => (
            <Reveal key={c.key} className="reveal" >
              <Link to={`/shop/${c.key}`} data-testid={`category-${c.key}`}
                className="group relative block aspect-[3/4] overflow-hidden">
                <img src={c.img} alt={c.label} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-espresso via-espresso/20 to-transparent" />
                <div className="absolute bottom-0 p-6">
                  <h3 className="font-serif text-2xl text-cream">{c.label}</h3>
                  <p className="text-cream/70 text-sm mt-1">{c.copy}</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-gold text-sm">
                    Explore <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Quiz CTA */}
      <section className="relative py-24 overflow-hidden">
        <img src="https://images.pexels.com/photos/18408805/pexels-photo-18408805.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1600"
          alt="Spa" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-espresso/80" />
        <Reveal className="relative max-w-3xl mx-auto text-center px-5">
          <Sparkles className="text-gold mx-auto mb-4" size={28} />
          <h2 className="font-serif text-3xl sm:text-5xl text-cream leading-tight">Not Sure Where to Begin?</h2>
          <p className="text-cream/75 mt-4 text-lg font-light">Answer a few questions and our AI concierge will match you with the perfect setup — plus a complete bundle to go with it.</p>
          <Link to="/quiz" data-testid="home-quiz-cta"
            className="mt-8 inline-flex items-center gap-2 bg-gold text-espresso px-8 py-3.5 font-medium hover:bg-amber transition-colors">
            Find Your Perfect Match <ArrowRight size={18} />
          </Link>
        </Reveal>
      </section>

      {/* Best sellers */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20">
        <Reveal className="flex items-end justify-between">
          <div>
            <p className="overline">Most Loved</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-cream mt-2">Best Sellers</h2>
          </div>
          <Link to="/shop" className="hidden sm:flex items-center gap-1 text-gold text-sm hover:gap-2 transition-all">
            View all <ArrowRight size={15} />
          </Link>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
          {bestSellers.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      {/* Commercial CTA */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 pb-20">
        <Reveal className="grid md:grid-cols-2 bg-charcoal border border-white/10 overflow-hidden">
          <div className="p-10 lg:p-14 flex flex-col justify-center">
            <p className="overline text-forest">For Business</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-cream mt-3">Commercial Wellness Solutions</h2>
            <p className="text-cream/70 mt-4 leading-relaxed">Outfit your gym, hotel or spa with commercial-grade saunas, cold plunges and hot tubs. Volume pricing, dedicated account management and white glove installation.</p>
            <div className="flex flex-wrap gap-3 mt-7">
              <Link to="/commercial" data-testid="home-commercial-cta" className="bg-gold text-espresso px-6 py-3 font-medium hover:bg-amber transition-colors">Request a Quote</Link>
              <button onClick={() => openConsultation("Commercial inquiry")} className="border border-cream/30 text-cream px-6 py-3 hover:border-cream transition-colors">Talk to Our Team</button>
            </div>
          </div>
          <div className="relative min-h-[280px]">
            <img src="https://images.unsplash.com/photo-1572168400468-62e1b3209d7d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Commercial sauna" className="absolute inset-0 w-full h-full object-cover" />
          </div>
        </Reveal>
      </section>
    </div>
  );
}
